
interface Specification {
  label: string;
  value: string;
}

interface ProductSpecificationsProps {
  specifications: Specification[];
}

export default function ProductSpecifications({ specifications }: ProductSpecificationsProps) {
  return (
    <section className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Technical Specifications</h2>
          <p className="text-lg text-gray-600">
            Detailed technical specifications for our emergency exit signs
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-50 rounded-2xl overflow-hidden">
            <div className="bg-red-600 text-white p-6">
              <h3 className="text-xl font-semibold">Emergency Exit Sign Specifications</h3>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
                {specifications.map((spec, index) => (
                  <div key={index} className="flex justify-between items-center py-3 border-b border-gray-200 last:border-b-0">
                    <span className="font-medium text-gray-700">{spec.label}:</span>
                    <span className="text-gray-900 font-semibold">{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Performance Features */}
          <div className="mt-12 bg-gray-50 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">Performance Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">30m</div>
                <div className="text-sm text-gray-600">Viewing Distance</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">50,000</div>
                <div className="text-sm text-gray-600">LED Hours</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600 mb-2">30 Days</div>
                <div className="text-sm text-gray-600">Test Interval</div>
              </div>
            </div>
          </div>

          {/* Mounting Options */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
              <i className="ri-building-line text-blue-600 text-3xl mb-3"></i>
              <h4 className="font-semibold text-blue-900 mb-2">Wall Mount</h4>
              <p className="text-blue-800 text-sm">Surface or recessed wall mounting options</p>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <i className="ri-home-line text-green-600 text-3xl mb-3"></i>
              <h4 className="font-semibold text-green-900 mb-2">Ceiling Mount</h4>
              <p className="text-green-800 text-sm">Suspended or surface ceiling installation</p>
            </div>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 text-center">
              <i className="ri-corner-up-right-line text-purple-600 text-3xl mb-3"></i>
              <h4 className="font-semibold text-purple-900 mb-2">Corner Mount</h4>
              <p className="text-purple-800 text-sm">Corner bracket for optimal visibility</p>
            </div>
          </div>

          {/* Installation Notes */}
          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <i className="ri-information-line text-amber-600 text-xl mt-1"></i>
              <div>
                <h4 className="font-semibold text-amber-900 mb-2">Installation Guidelines</h4>
                <ul className="text-amber-800 text-sm space-y-1">
                  <li>• Install at minimum 2.1m height from floor level</li>
                  <li>• Ensure clear line of sight from all areas</li>
                  <li>• Connect to emergency lighting circuit</li>
                  <li>• Test functionality after installation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
